import React from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';

import { scale, Element, notchTop, scaleFactor, isChromeOS } from '@components/frame';
import { isWeb, isAndroid, isIOS, isPhone } from '@helpers';
import { font } from '@styles/fonts';

import BackButton from '@components/common/backButton';

const baseHeight = 56;

const getMargin = () => {
  if (isAndroid) {
    return 0;
  }
  if (isIOS) {
    return notchTop > 0 ? notchTop : 0;
  }

  return 0;
};

const getPadding = () => {
  if (isIOS) {
    return 0;
  }
  if (isChromeOS) {
    return scale(15);
  }
  return 0;
};

export const getHeight = () => {
  const margin = getMargin();
  const padding = getPadding();
  const height = scale(baseHeight) + scale(margin) + padding;
  return height;
};

export default (props) => {
  const {
    fadeAnim,
    C,
    text,
    scrollRef,
    startFrom = 4,
    startBGFrom = 0,
    animated = true,
    actions = [],
    back = false,
    onBackPress = false,
    backArrow = true,
    backText = 'Back',
    TextComponent,
    backgroundColor = 'Background / Light / System',
    borderBottomWidth = StyleSheet.hairlineWidth,
    customMargin,
    zIndex,
    setActionsModalOpen,
    position = 'absolute',
    children,
    customPadding,
  } = props;

  const actionZIndex = 1100;

  const color1 = C(backgroundColor).replace('1)', '0)');
  const color2 = C(backgroundColor).replace('1)', '0.5)');
  const color3 = C(backgroundColor);

  const margin = customMargin ?? getMargin();
  const padding = customPadding ?? getPadding();

  return (
    <Element
      {...{
        config: {
          customPosition: position,
          height: baseHeight + margin,
          borderBottomWidth: borderBottomWidth,
          width: '100%',
          zIndex: zIndex || 1000,
          animated: animated,
          opacity: animated
            ? fadeAnim.interpolate({
                inputRange: [scale(startFrom) - 0.5, scale(startFrom), scale(startFrom) + 0.5],
                outputRange: [0, 0.8, 1],
                extrapolate: 'clamp',
              })
            : 1,
          backgroundColor:
            animated && startBGFrom
              ? fadeAnim.interpolate({
                  inputRange: [scale(startBGFrom) - 5, scale(startBGFrom), scale(startBGFrom) + 5],
                  outputRange: [color1, color2, color3],
                  extrapolate: 'clamp',
                })
              : C(backgroundColor),
          borderBottomColor:
            animated && startBGFrom
              ? fadeAnim.interpolate({
                  inputRange: [scale(startBGFrom) - 5, scale(startBGFrom), scale(startBGFrom) + 5],
                  outputRange: ['transparent', 'transparent', C('Hairline / Light / No trans')],
                  extrapolate: 'clamp',
                })
              : C('Hairline / Light / No trans'),
        },
      }}
    >
      <Element
        key="AnimatedHeader"
        {...{
          config: {
            height: baseHeight,
            width: '100%',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: zIndex || 1000,
            marginTop: scale(margin),
            paddingTop: padding,
          },
        }}
      >
        {back && (
          <Element
            {...{
              config: {
                position: {
                  left: 4,
                },
                alignContent: 'center',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: actionZIndex,
                bottom: scale(isWeb ? 17 : 10, false),
              },
            }}
          >
            <BackButton {...{ backArrow, text: backText, onPress: onBackPress }} />
          </Element>
        )}
        {actions.map((Item) => (
          <Item.component key={Item.key} {...{ padding, zIndex: actionZIndex, baseHeight, C, setActionsModalOpen }} />
        ))}
        <TouchableOpacity
          style={[
            {
              width: '100%',
              height: '100%',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: zIndex + 1 || 1001,
            },
            isWeb && {
              cursor: 'auto',
            },
          ]}
          activeOpacity={1}
          onPress={scrollRef && scrollRef.current ? () => scrollRef.current.scrollTo({ y: 0 }) : () => {}}
        >
          {TextComponent ? (
            <TextComponent />
          ) : (
            <View
              style={[
                {
                  bottom: scale(isWeb ? 17 : 10, false),
                  width: '100%',
                  position: 'absolute',
                },
                isPhone && {
                  paddingHorizontal: scale(100),
                },
              ]}
            >
              <Text
                numberOfLines={1}
                style={[
                  font.Headline,
                  {
                    color: C('Label / Light / Primary'),
                    width: '100%',
                    textAlign: 'center',
                  },
                ]}
              >
                {text}
              </Text>
            </View>
          )}
        </TouchableOpacity>
        {children}
      </Element>
    </Element>
  );
};
